import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';

import { AccountService } from 'src/app/auth/account.service';
import { Account } from 'src/app/auth/account.model';
import { ApplicationConfigService } from 'src/app/core/config/application-config.service';

import { MockDb } from '../mock-db';

const MOCK_LATENCY_MS = 300;

/**
 * Stands in for AccountService when `environment.useMock` is true.
 *
 * Only `fetch()` is overridden. `identity()` in the parent wraps it with
 * `tap(res => this.authenticate(res.data))` + `shareReplay()`, so the mock must return the full
 * response envelope — `{ responseCode, data }` — not a bare Account.
 */
@Injectable()
export class MockAccountService extends AccountService {
  constructor(http: HttpClient, applicationConfigService: ApplicationConfigService) {
    super(http, applicationConfigService);
  }

  override fetch(): Observable<Account> {
    const user = MockDb.getCurrentUser();

    if (!user) {
      // No mock session: behave like an unauthenticated /user/me so AccountGuard redirects to login.
      return throwError(() => new Error('No mock session')) as unknown as Observable<Account>;
    }

    const envelope = {
      responseCode: 'S100000',
      responseMessage: 'Operation successful',
      data: {
        username: user.username,
        userIdentity: user.userIdentity,
        active: true,
        email: user.email,
        fullName: user.fullName,
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNo: user.phoneNo,
        branchCode: user.branchCode,
        branchName: user.branchName,
        roleList: [{ roleId: user.role, roleName: user.roleLabel }],
      },
    };

    return of(envelope).pipe(delay(MOCK_LATENCY_MS)) as unknown as Observable<Account>;
  }
}
